"use strict";
//
// Copyright © 2024 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
Object.defineProperty(exports, "__esModule", { value: true });
exports.getClient = getClient;
const core_1 = require("@hcengineering/core");
const utils_1 = require("./utils");
/** @public */
function getClient(workspaceId, token, collaboratorUrl) {
    const url = collaboratorUrl.replaceAll('wss://', 'https://').replace('ws://', 'http://');
    return new CollaboratorClientImpl(workspaceId, token, url);
}
class CollaboratorClientImpl {
    workspace;
    token;
    collaboratorUrl;
    constructor(workspace, token, collaboratorUrl) {
        this.workspace = workspace;
        this.token = token;
        this.collaboratorUrl = collaboratorUrl;
    }
    async rpc(document, method, payload, query) {
        const workspace = this.workspace;
        const documentId = (0, utils_1.encodeDocumentId)(workspace, document);
        let path = `/rpc/${encodeURIComponent(documentId)}`;
        if (query !== undefined && Object.keys(query).length > 0) {
            const searchParams = new URLSearchParams();
            for (const [key, value] of Object.entries(query)) {
                if (value !== undefined) {
                    searchParams.append(key, String(value));
                }
            }
            const queryString = searchParams.toString();
            if (queryString.length > 0) {
                path += `?${queryString}`;
            }
        }
        const url = (0, core_1.concatLink)(this.collaboratorUrl, path);
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + this.token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ method, payload })
        });
        if (!res.ok) {
            throw new Error('HTTP error ' + res.status);
        }
        const result = await res.json();
        if (result.error != null) {
            throw new Error(result.error);
        }
        return result;
    }
    async getMarkup(document, source) {
        const payload = {
            source: source !== null ? source : undefined
        };
        const res = await retry(3, async () => {
            return await this.rpc(document, 'getContent', payload);
        }, 50);
        return res.content[document.objectAttr] ?? '';
    }
    async createMarkup(document, markup, options) {
        const content = {
            [document.objectAttr]: markup
        };
        const query = options?.silent === true ? { silent: true } : undefined;
        const res = await retry(3, async () => {
            return await this.rpc(document, 'createContent', { content }, query);
        }, 50);
        return res.content[document.objectAttr];
    }
    async updateMarkup(document, markup, options) {
        const content = {
            [document.objectAttr]: markup
        };
        const query = options?.silent === true ? { silent: true } : undefined;
        await retry(3, async () => {
            await this.rpc(document, 'updateContent', { content }, query);
        }, 50);
    }
    async getVersions(document) {
        const res = await retry(3, async () => {
            return await this.rpc(document, 'getVersions', {});
        }, 50);
        return res.versions;
    }
    async getVersionContent(document, blobId) {
        const res = await retry(3, async () => {
            return await this.rpc(document, 'getVersionContent', {
                blobId
            });
        }, 50);
        return res.content;
    }
    async copyContent(source, target, content, options) {
        const markup = await this.getMarkup(source, content);
        await this.updateMarkup(target, markup, options);
    }
}
async function retry(retries, op, delay = 100) {
    let error;
    while (retries > 0) {
        retries--;
        try {
            return await op();
        }
        catch (err) {
            error = err;
            if (retries !== 0) {
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }
    }
    throw error;
}
//# sourceMappingURL=client.js.map